
import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  FormControlLabel,
  FormGroup,
  Grid,
  ModalProps,
  Switch,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CANNOT_UPDATE_2FA, PAGE_TITLE, REAUTHENTICATION_MESSAGE } from 'common/constants/login';
import { useStore } from 'common/store';
import { LayoutContextValues, PageBreadcrumb, UseStoreValues } from 'common/types/store';
import { BackupCodes } from 'components';
import { LayoutContext } from 'context';
import LogoutButton from 'pages/Login/components/LogoutBtn';
import Otp from 'pages/Login/components/OTP';
import { AuthService } from 'services';
import { useResponse } from 'services/hooks';
import { Logger } from 'utilities';
import { User2FAStatus } from './User2FA.props';

const StyledHeading = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(15),
  fontWeight: 700,
}));

const StyledMessageContainer = styled(Grid)({
  marginTop: '1.5rem',
});

const StyledMessage = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
  marginTop: '1.5rem',
  marginRight: '1rem',
  display: 'inline-block',
}));

const StyledFormGroup = styled(FormGroup)({
  paddingBottom: '1.5rem',
});

const User2FA = () => {
  const [user2faStatus, setUser2faStatus] = useState<User2FAStatus>({
    user_has_totp_device: false,
    confirmed: false,
    persistent_id: '',
  });
  const [backupCodes, setBackupCodes] = useState([]);
  const [showOTP, setShowOTP] = useState(false);
  const [isGeneratingBackupCodes, setIsGeneratingBackupCodes] = useState(false);
  const [displayReauthenticate, setDisplayReauthenticate] = useState(false);
  const [{ user }] = useStore() as unknown as UseStoreValues;

  const { processErrorResponse } = useResponse();
  const { setPageTitle, setPageBreadcrumbs } = useContext(LayoutContext) as unknown as LayoutContextValues;

  const handleDialogClose: ModalProps['onClose'] = (_event, reason) => {
    if (reason && reason === 'backdropClick') {
      setShowOTP(false);
    }
  };

  const handle2faChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    // Launch the OTP dialog if the user is setting up 2FA for the first time
    if (!user2faStatus.user_has_totp_device && checked) {
      setShowOTP(true);
      return;
    }

    const updateTOTP = async () => {
      const authService = AuthService.getInstance();
      try {
        if (!checked) {
          await authService.deleteTOTP();
          setUser2faStatus({
            user_has_totp_device: false,
            confirmed: false,
          });
        } else {
          setShowOTP(true);
        }
      } catch (err) {
        processErrorResponse({
          error: err,
          defaultErrorMessage: CANNOT_UPDATE_2FA,
        });
        // Revert the switch status to the previous state if there was an error
        setUser2faStatus(prevStatus => ({
          ...prevStatus,
          confirmed: !checked,
        }));
      }
    };

    if (user2faStatus.user_has_totp_device) {
      updateTOTP();
    }
  };

  const generateNewBackupCodes = useCallback(async () => {
    setIsGeneratingBackupCodes(true);

    const authService = AuthService.getInstance();

    try {
      const response = await authService.createBackupCodes(user2faStatus);
      if (response) {
        setBackupCodes(response.tokens);
      }
    } catch (error: unknown) {
      // If we recieve a Forbidden error we need to reauthenticate the user
      if ((error as { status?: number })?.status === 403) {
        setDisplayReauthenticate(true);
      }
      Logger.error('Error generating backup codes', error);
    }
    setIsGeneratingBackupCodes(false);
  }, [user2faStatus]);

  const breadcrumbs: PageBreadcrumb[] = useMemo(() => {
    if (user) {
      return [
        {
          title: PAGE_TITLE,
        },
        {
          title: user.email ?? '',
        },
      ];
    }

    return [];
  }, [user]);

  useEffect(() => {
    setPageTitle?.(PAGE_TITLE);
    setPageBreadcrumbs?.(breadcrumbs);

    return () => {
      setPageTitle?.(null);
      setPageBreadcrumbs?.(null);
    };
  }, [breadcrumbs, setPageTitle, setPageBreadcrumbs]);

  useEffect(() => {
    const getUserTOTP = async () => {
      try {
        const authService = AuthService.getInstance();
        const response = await authService.getTOTP();

        if (response) {
          setUser2faStatus(response);
        }
      } catch (error: unknown) {
        Logger.error('Error getting TOTP', error);
        throw new Error(error as string);
      }
    };

    if (user) {
      getUserTOTP();
    }
  }, [user]);

  useEffect(() => {
    const getUserBackupCodes = async () => {
      const authService = AuthService.getInstance();

      try {
        const response = await authService.getBackupCodes();

        if (response) {
          setBackupCodes(response.tokens);
          if (response.tokens.length === 0) {
            await generateNewBackupCodes();
          }
        }
      } catch (error: unknown) {
        // If we receive a 404 error - "No TOTP device found", we need to generate new backup codes
        if ((error as { status?: number })?.status === 404) {
          await generateNewBackupCodes();
        }
      }
    };

    if (user2faStatus.user_has_totp_device) {
      getUserBackupCodes();
    }
  }, [generateNewBackupCodes, user2faStatus]);

  return (
    <>
      <Accordion>
        <AccordionSummary className="accordionSummary" expandIcon={<ExpandMoreIcon className="icon" />}>
          <StyledHeading>Activate/Deactivate {PAGE_TITLE}</StyledHeading>
        </AccordionSummary>
        <AccordionDetails>
          <StyledFormGroup row>
            <FormControlLabel
              control={
                <Switch
                  checked={user2faStatus.confirmed && backupCodes.length > 0}
                  onChange={handle2faChange}
                  name="user2fa"
                  color="primary"
                  disabled={false}
                  disableRipple
                />
              }
              label={
                <Typography variant="body2" color="textPrimary">
                  Enable {PAGE_TITLE}
                </Typography>
              }
              labelPlacement="start"
            />
          </StyledFormGroup>
        </AccordionDetails>
      </Accordion>
      {displayReauthenticate
        ? user2faStatus?.confirmed && (
          <StyledMessageContainer alignItems="center" container>
            <Grid item>
              <StyledMessage>{REAUTHENTICATION_MESSAGE}</StyledMessage>
            </Grid>

            <Grid item>
              <LogoutButton />
            </Grid>
          </StyledMessageContainer>
        )
        : user2faStatus?.confirmed
          && backupCodes.length > 0 && (
          <Accordion>
            <AccordionSummary className="accordionSummary" expandIcon={<ExpandMoreIcon className="icon" />}>
              <StyledHeading>{PAGE_TITLE} Backup Codes</StyledHeading>
            </AccordionSummary>

            <AccordionDetails>
              <BackupCodes
                backupCodes={backupCodes}
                disabledGenerate={!user2faStatus?.confirmed}
                handleGenerateNewCodes={generateNewBackupCodes}
                isGeneratingBackupCodes={isGeneratingBackupCodes}
                username={user?.email}
              />
            </AccordionDetails>
          </Accordion>
        )}
      <Dialog open={showOTP} onClose={handleDialogClose}>
        <Otp
          userHas2FA={false}
          showActionButtons={false}
          setDeviceData={setUser2faStatus}
          setDisplayReauthenticate={setDisplayReauthenticate}
        />
      </Dialog>
    </>
  );
};

export default User2FA;
